import chartIconDarkSvg from './assets/darkModeIcons/chart-icon-dark.svg';
import diagramIconDarkSvg from './assets/darkModeIcons/diagram-icon-dark.svg';
import infographicIconDarkSvg from './assets/darkModeIcons/infographic-dark.svg';
import visualAidIconDarkSvg from './assets/darkModeIcons/visual-aid-dark.svg';
import chartIconSvg from './assets/lightModeIcons/chart-icon.svg';
import diagramIconSvg from './assets/lightModeIcons/diagram-icon.svg';
import infographicIconSvg from './assets/lightModeIcons/infographic-light.svg';
import visualAidIconSvg from './assets/lightModeIcons/visual-aid.svg';
import messages from './messages';
import type { RemixOption } from './types';

/**
 * All available remix options with their sub-types (variants).
 *
 * Sub-type values match the backend `dynamicUiSubtype` parameter
 * used in the remix-content minion (see edit-with-rovo-bar).
 */
export const REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'chart',
		displayName: 'chart',
		icon: chartIconSvg,
		iconDark: chartIconDarkSvg,
		labelMessage: messages.remixOptionChart,
		iconAltMessage: messages.chartIconAlt,
		tooltipMessage: messages.chartTooltip,
		testId: 'remix-overlay-option-chart',
		subTypes: [
			{
				value: 'line-chart',
				displayName: 'line chart',
				labelMessage: messages.subTypeLineChart,
				testId: 'remix-sub-type-line-chart',
			},
			{
				value: 'area-chart',
				displayName: 'area chart',
				labelMessage: messages.subTypeAreaChart,
				testId: 'remix-sub-type-area-chart',
			},
			{
				value: 'bar-chart',
				displayName: 'bar chart',
				labelMessage: messages.subTypeBarChart,
				testId: 'remix-sub-type-bar-chart',
			},
			{
				value: 'funnel-chart',
				displayName: 'funnel chart',
				labelMessage: messages.subTypeFunnelChart,
				testId: 'remix-sub-type-funnel-chart',
			},
			{
				value: 'pie-chart',
				displayName: 'pie chart',
				labelMessage: messages.subTypePieChart,
				testId: 'remix-sub-type-pie-chart',
			},
			{
				value: 'gantt-chart',
				displayName: 'Gantt chart',
				labelMessage: messages.subTypeGanttChart,
				testId: 'remix-sub-type-gantt-chart',
			},
		],
	},
	{
		type: 'diagram',
		displayName: 'diagram',
		icon: diagramIconSvg,
		iconDark: diagramIconDarkSvg,
		labelMessage: messages.remixOptionDiagram,
		iconAltMessage: messages.diagramIconAlt,
		tooltipMessage: messages.diagramTooltip,
		testId: 'remix-overlay-option-diagram',
		subTypes: [
			{
				value: 'mindmap',
				displayName: 'mind map',
				labelMessage: messages.subTypeMindMap,
				testId: 'remix-sub-type-mindmap',
			},
			{
				value: 'flowchart',
				displayName: 'flowchart',
				labelMessage: messages.subTypeFlowChart,
				testId: 'remix-sub-type-flowchart',
			},
		],
	},
	{
		type: 'infographic',
		displayName: 'infographic',
		icon: infographicIconSvg,
		iconDark: infographicIconDarkSvg,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
		subTypes: [
			{
				value: 'corporate-modern',
				displayName: 'modern style infographic (corporate modern)',
				labelMessage: messages.subTypeModernStyle,
				testId: 'remix-sub-type-corporate-modern',
			},
			{
				value: 'corporate-doodle',
				displayName: 'sketch style infographic (corporate doodle)',
				labelMessage: messages.subTypeSketchStyle,
				testId: 'remix-sub-type-corporate-doodle',
			},
		],
	},
	{
		type: 'visual-aid',
		displayName: 'visualization',
		icon: visualAidIconSvg,
		iconDark: visualAidIconDarkSvg,
		labelMessage: messages.remixOptionVisualization,
		iconAltMessage: messages.visualizationIconAlt,
		tooltipMessage: messages.visualizationTooltip,
		testId: 'remix-overlay-option-visual-aid',
		subTypes: [
			{
				value: 'flip-cards',
				displayName: 'flip card',
				labelMessage: messages.subTypeFlipCard,
				testId: 'remix-sub-type-flip-cards',
			},
			{
				value: 'quadrant',
				displayName: 'quadrant',
				labelMessage: messages.subTypeQuadrant,
				testId: 'remix-sub-type-quadrant',
			},
			{
				value: 'tree-map',
				displayName: 'tree map',
				labelMessage: messages.subTypeTreeMap,
				testId: 'remix-sub-type-tree-map',
			},
			{
				value: 'horizontal-timeline',
				displayName: 'timeline',
				labelMessage: messages.subTypeTimeline,
				testId: 'remix-sub-type-horizontal-timeline',
			},
			{
				value: 'map',
				displayName: 'map',
				labelMessage: messages.subTypeGeographicalMap,
				testId: 'remix-sub-type-map',
			},
			{
				value: 'org-chart',
				displayName: 'org chart',
				labelMessage: messages.subTypeOrgChart,
				testId: 'remix-sub-type-org-chart',
			},
		],
	},
];

/**
 * Returns the human-readable display name for a type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getTypeDisplayName(typeValue: string): string {
	const match = REMIX_OPTIONS.find((option) => option.type === typeValue);
	return match?.displayName ?? typeValue.replace(/-/g, ' ');
}

/**
 * Returns the human-readable display name for a sub-type value.
 * Falls back to replacing hyphens with spaces if the value is not found.
 *
 * Used by consumers (e.g. Confluence) to construct natural-language prompts.
 */
export function getSubTypeDisplayName(subTypeValue: string): string {
	for (const option of REMIX_OPTIONS) {
		const match = option.subTypes.find((st) => st.value === subTypeValue);
		if (match) {
			return match.displayName;
		}
	}
	return subTypeValue.replace(/-/g, ' ');
}
